import type { AuditGroup, ChangesSinceLastVisitAuditItem } from '../types'
import type { Audit } from '@app/shared/types/sf-data'
import { isChangesSinceLastVisitAuditItem } from '../predicates'

import I18n from 'i18n-js'

const MINIMUM_GROUP_SIZE = 2

export const groupAudits = (
  audits: Array<Audit | ChangesSinceLastVisitAuditItem>
): Array<Audit | AuditGroup | ChangesSinceLastVisitAuditItem> => {
  if (!audits.length) {
    return []
  }

  const auditsWithGroups = []

  let consecutiveSimilarAudits = 1
  let groupUsername
  let insertedGroups = 0

  let previousGroupingKey = isChangesSinceLastVisitAuditItem(audits[0])
    ? null
    : audits[0].groupingKey

  // Neat trick to keep the logic simple:
  // iterate one step further (like an extra dummy item at the end of the real list),
  // to execute the group-or-single-item logic for the previous item, which is the last real item in the list
  for (let i = 1; i <= audits.length; i++) {
    const audit = audits[i] || {}
    const currentGroupingKey = isChangesSinceLastVisitAuditItem(audit)
      ? null
      : audit.groupingKey

    const sequentialGroupingKeys =
      currentGroupingKey &&
      previousGroupingKey &&
      currentGroupingKey === previousGroupingKey
    // consecutive elements with the same non-nil grouping key => keep track of the sequence count
    if (sequentialGroupingKeys) {
      consecutiveSimilarAudits += 1
      groupUsername = isChangesSinceLastVisitAuditItem(audit)
        ? null
        : audit.username
    }
    // a. sequence is broken - if it has min 2 items, insert a group audit, nesting the audits sequence under it
    // b. last item in the array => check if we have a min-2 sequence and insert a group audit (there's no next element to "look back" and insert the group)
    if (!sequentialGroupingKeys) {
      if (consecutiveSimilarAudits >= MINIMUM_GROUP_SIZE) {
        // NOTE: this is now "hardcoded" for TPs. When/if we'll support more group types, this will need to be dynamic
        const message = I18n.t('audit.group_headers.text_property', {
          username: groupUsername,
          count: consecutiveSimilarAudits,
        })
        // if a group ends at the end of the list, it means the current (last) item is also in the group,
        // so we shift the slice by 1 at both ends (normally, the sequence is detected as "broken" at the next item)
        const auditsForGroup = audits.slice(i - consecutiveSimilarAudits, i)

        const group: AuditGroup = {
          id: `${previousGroupingKey}_${insertedGroups}`,
          message,
          audits: auditsForGroup,
          kind: 'audit-log-group',
          sortTimestamp: auditsForGroup[0].sortTimestamp,
        }

        auditsWithGroups.push(group)

        insertedGroups += 1
      } else {
        auditsWithGroups.push(audits[i - 1])
      }
      // reset group username and sequence size
      groupUsername = null
      consecutiveSimilarAudits = 1
    }

    previousGroupingKey = currentGroupingKey
  }

  return auditsWithGroups
}
